import React, { useEffect, useState } from "react";
import { FiShoppingBag } from "react-icons/fi";
import axios from "../../Utils/axiosInstance";
import toast from "react-hot-toast";
import { assets } from './../../Assets/assets';
import OrderDetailCard from "../../Components/UserSideComponents/Orders/OrderDetailCard";
import { useLocation, useNavigate } from "react-router-dom";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import OrderListItem from "../../Components/UserSideComponents/Quotations/OrderListItem";
import TransactionsHistoryTab from "../../Components/UserSideComponents/Orders/TransactionsHistoryTab";
import { useAuth } from "../../Components/Context/AuthContext";

// ─── Skeleton ─────────────────────────────────────────
const OrderSkeleton = () => (
  <div className="p-4 rounded-lg border border-gray-200 animate-pulse">
    <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
    <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-20 bg-gray-200 rounded" />
  </div>
);

// ─── Empty State ──────────────────────────────────────
const EmptyOrders = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <img
      src={assets.noordervector}
      alt="No Orders"
      className="w-64 md:w-80 mb-5 opacity-90"
    />
    <h2 className="text-xl font-semibold text-gray-600">
      No Orders Found
    </h2>
    <p className="text-sm text-gray-400 mt-1">
      You haven't placed any orders yet.
    </p>
  </div>
);

// ─── Error State ──────────────────────────────────────
const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <p className="text-red-500 font-medium mb-3">
      Failed to load orders
    </p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-primaryColor text-white rounded-lg text-sm"
    >
      Retry
    </button>
  </div>
);

// ─── Main Component ───────────────────────────────────
const Orders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user?.role === "supplier") {
      navigate("/supplier-orders", { replace: true });
    }
  }, [user, navigate]);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [showTransactions, setShowTransactions] = useState(false);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(false);

      const { data } = await axios.get(`/order/get-orders-created-by-user?page=${page}&limit=${limit}`);
      const ordersList = data.data?.orders || data.data || [];
      setOrders(ordersList);
      setPagination(data.data?.pagination || null);

      // Auto-select first order on desktop
      if (ordersList.length > 0 && window.innerWidth >= 768 && !selectedOrder) {
        setSelectedOrder(ordersList[0]);
      }
    } catch (err) {
      setError(true);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");

    if (highlightId && orders.length) {
      const foundOrder = orders.find((o) => o._id === highlightId);

      if (foundOrder) {
        setSelectedOrder(foundOrder);
      }
    }
  }, [location.search, orders]);

  useEffect(() => {
    fetchOrders();
  }, [page, limit]);

  const filteredOrders = orders.filter(order => {
    const searchLower = searchQuery.toLowerCase();
    return (
      order.orderNumber?.toLowerCase().includes(searchLower) ||
      order._id?.toLowerCase().includes(searchLower) ||
      order.product?.name?.toLowerCase().includes(searchLower)
    );
  });

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    // Scroll to top on mobile
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 py-4 sm:py-6 mb-16 md:mb-0">

        {/* Header - Only on mobile when no order selected, always on desktop */}
        <div className={`mb-4 sm:mb-5 ${selectedOrder && window.innerWidth < 768 ? 'hidden' : 'block'}`}>
          <h1 className="text-xl flex items-center gap-2 sm:text-2xl font-bold text-primaryColor mb-1">
            <FiShoppingBag />My Orders
          </h1>
          <p className="text-gray-500 text-sm">
            View your order history and manage your orders.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <AppLoader message="Loading Your Orders..." />
        )}

        {/* Error State */}
        {!loading && error && <ErrorState onRetry={fetchOrders} />}

        {/* Empty State */}
        {!loading && !error && orders.length === 0 && <EmptyOrders />}

        {/* Orders Layout */}
        {!loading && !error && orders.length > 0 && (
          <>
            {/* Mobile View - List or Detail */}
            <div className="md:hidden">
              {!selectedOrder ? (
                <>
                  {/* Search */}
                  <div className="mb-4">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search order ID"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>

                  {/* Orders List */}
                  <div className="space-y-3 mb-4">
                    {filteredOrders.map((order) => (
                      <OrderListItem
                        key={order._id}
                        order={order}
                        isActive={false}
                        onClick={() => handleOrderSelect(order)}
                        quotationCount={order?.approvedQuotationCount}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {pagination && (
                    <Pagination
                      pagination={pagination}
                      page={page}
                      setPage={setPage}
                      limit={limit}
                      setLimit={setLimit}
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Back Button */}
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="flex items-center gap-2 text-primaryColor mb-4 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Orders
                  </button>

                  {/* Order Detail */}
                  {showTransactions ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h2 className="text-base font-bold text-gray-800 dark:text-white flex items-center gap-2">
                          📜 View Transaction History
                        </h2>
                        <button
                          onClick={() => setShowTransactions(false)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-white/20 transition text-xs font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Back
                        </button>
                      </div>
                      <TransactionsHistoryTab orderId={selectedOrder?._id} />
                    </div>
                  ) : (
                    <OrderDetailCard order={selectedOrder} onShowTransactions={() => setShowTransactions(true)} />
                  )}
                </>
              )}
            </div>

            {/* Desktop View - Split Layout */}
            <div className="hidden md:block">
              <div className="grid md:grid-cols-12 gap-5">
                {/* Left Sidebar - Orders List */}
                <div className="md:col-span-4 lg:col-span-3 space-y-4 sticky top-16 h-[calc(100vh-4rem)] overflow-hidden">
                  <div className="h-full overflow-y-scroll">
                    {/* Search */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Search order ID"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-sm"
                      />
                      <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>

                    {/* Orders List */}
                    <div className="space-y-3 mb-3">
                      {filteredOrders.map((order) => (
                        <OrderListItem
                          key={order._id}
                          order={order}
                          isActive={selectedOrder?._id === order._id}
                          onClick={() => setSelectedOrder(order)}
                          quotationCount={order?.approvedQuotationCount}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                      <div className="pt-3 border-t border-gray-200">
                        <Pagination
                          pagination={pagination}
                          page={page}
                          setPage={setPage}
                          limit={limit}
                          setLimit={setLimit}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Panel - Order Detail */}
                <div className="md:col-span-8 lg:col-span-9 bg-white rounded-lg border border-gray-200 p-5">
                  {showTransactions ? (
                    <div className="space-y-4">
                      {/* Inline Transaction View for Desktop */}
                      <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                          <span className="text-xl">📜</span>View Transaction History
                        </h2>
                        <button
                          onClick={() => setShowTransactions(false)}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-white/20 transition text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Back to Order
                        </button>
                      </div>
                      <TransactionsHistoryTab orderId={selectedOrder?._id} />
                    </div>
                  ) : (
                    <OrderDetailCard order={selectedOrder} onShowTransactions={() => setShowTransactions(true)} />
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Orders;